import { Suspense, lazy, type ReactElement, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../app/hooks";
import AppShell from "../components/layout/AppShell";
import { canAccessPath, getDefaultAuthorizedPath } from "../features/auth/access";
import { logout, resolveSession } from "../features/auth/authSlice";
import { registerUnauthorizedHandler, resolveAuthSession } from "../lib/api";
import { clearStoredSession, persistSession } from "../features/auth/storage";
import Client360Page from "../pages/clients/Client360Page";
import ExpensesLayout from "../pages/expenses/ExpensesLayout";
import { routePaths } from "./navigation";

const ClientsPage = lazy(() => import("../pages/clients"));

const ClientHistoryPage = lazy(() => import ("../pages/clients/ClientHistoryPage"));
const B2BPage = lazy(() => import("../pages/b2b"));
const WholesalePromotionsPage = lazy(() => import("../pages/b2b/promotions/WholesalePromotionsPage"));
const WholesaleSuppliersPage = lazy(() => import("../pages/b2b/suppliers/WholesaleSuppliersPage"));
const HomePage = lazy(() => import("../pages/home"));
const DashboardWorkspacePage = lazy(() => import("../pages/home/DashboardWorkspacePage"));
const LoginPage = lazy(() => import("../pages/login"));
const AdminAccessPage = lazy(() => import("../pages/admin"));
const MessagesPage = lazy(() => import("../pages/messages"));
const ForgotPasswordPage = lazy(() => import("../pages/password/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/password/ResetPasswordPage"));
const ChangePasswordPage = lazy(() => import("../pages/password/ChangePasswordPage"));
const LoyaltyPage = lazy(() => import("../pages/loyalty"));
const OrdersPage = lazy(() => import("../pages/orders"));
const OrderItemsPage = lazy(() => import("../pages/orders/orderItems"));
const CampaignsPage = lazy(() => import("../pages/campaigns"));
const ReportsPage = lazy(() => import("../pages/reports"));
const RetailCockpitPage = lazy(() => import("../pages/retail/RetailCockpitPage"));
const RetailFrequencyReportPage = lazy(() => import("../pages/retail/RetailFrequencyReportPage"));
const RetailPercentTargetsPage = lazy(() => import("../pages/retail/percentTargets/RetailPercentTargetsPage"));
const RetailDefectsSlivenPage = lazy(() => import("../pages/retail/defectsSliven/RetailDefectsSlivenPage"));
const OnlineCockpitPage = lazy(() => import("../pages/online/OnlineCockpitPage"));
const ExpensesTransportPage = lazy(() => import("../pages/expenses/ExpensesTransportReport"));
const ExpensesMarketingPage = lazy(() => import("../pages/expenses/ExpensesMarketingPage"));
const ExpensesSalariesPage = lazy(() => import("../pages/expenses/ExpensesSalariesPage"));

function RouteFallback() {
    return <div style={{ padding: "1rem" }}>Зареждане...</div>;
}

function LazyPage({ children }: { children: ReactElement }) {
    return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function ProtectedRoute({ children }: { children: ReactElement }) {
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const isReady = useAppSelector((state) => state.auth.isReady);
    const user = useAppSelector((state) => state.auth.user);
    const location = useLocation();

    if (!isReady) {
        return <RouteFallback />;
    }

    if (!isAuthenticated) {
        return (
            <Navigate
                to={routePaths.login}
                replace
                state={{ from: location.pathname }}
            />
        );
    }

    if (!canAccessPath(user, location.pathname)) {
        return <Navigate to={getDefaultAuthorizedPath(user)} replace />;
    }

    return children;
}

function PublicOnlyRoute({ children }: { children: ReactElement }) {
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const isReady = useAppSelector((state) => state.auth.isReady);
    const user = useAppSelector((state) => state.auth.user);

    if (!isReady) {
        return <RouteFallback />;
    }

    if (isAuthenticated) {
        return <Navigate to={getDefaultAuthorizedPath(user)} replace />;
    }

    return children;
}

export default function AppRoutes() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const isReady = useAppSelector((state) => state.auth.isReady);
    const user = useAppSelector((state) => state.auth.user);

    useEffect(() => {
        let isActive = true;

        const unregisterUnauthorizedHandler = registerUnauthorizedHandler(() => {
            if (!isActive) {
                return;
            }

            clearStoredSession();
            dispatch(logout());
        });

        void (async () => {
            const result = await resolveAuthSession();

            if (!isActive) {
                return;
            }

            if (result.status === "guest") {
                clearStoredSession();
                dispatch(resolveSession(null));
                return;
            }

            persistSession(result.user);
            dispatch(resolveSession(result.user));
        })();

        return () => {
            isActive = false;
            unregisterUnauthorizedHandler();
        };
    }, [dispatch]);

    if (!isReady) {
        return <RouteFallback />;
    }

    return (
        <Routes>
            <Route
                path={routePaths.root}
                element={
                    <Navigate
                        to={isAuthenticated ? getDefaultAuthorizedPath(user) : routePaths.login}
                        replace
                    />
                }
            />
            <Route
                path={routePaths.login}
                element={
                    <PublicOnlyRoute>
                        <LazyPage>
                            <LoginPage />
                        </LazyPage>
                    </PublicOnlyRoute>
                }
            />
            <Route
                path={routePaths.forgotPassword}
                element={
                    <PublicOnlyRoute>
                        <LazyPage>
                            <ForgotPasswordPage />
                        </LazyPage>
                    </PublicOnlyRoute>
                }
            />
            <Route
                path={routePaths.resetPassword}
                element={
                    <PublicOnlyRoute>
                        <LazyPage>
                            <ResetPasswordPage />
                        </LazyPage>
                    </PublicOnlyRoute>
                }
            />
            <Route
                element={
                    <ProtectedRoute>
                        <AppShell />
                    </ProtectedRoute>
                }
            >
                <Route
                    path={routePaths.dashboard}
                    element={
                        <LazyPage>
                            <HomePage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.dashboardWorkspace}
                    element={
                        <LazyPage>
                            <DashboardWorkspacePage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.adminAccess}
                    element={
                        <LazyPage>
                            <AdminAccessPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.messages}
                    element={
                        <LazyPage>
                            <MessagesPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.clients}
                    element={
                        <LazyPage>
                            <ClientsPage />
                        </LazyPage>
                    }
                />
                <Route path={routePaths.client360} element={<Client360Page />} />
                <Route
                    path={routePaths.clientHistory}
                    element={
                        <LazyPage>
                            <ClientHistoryPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.orders}
                    element={
                        <LazyPage>
                            <OrdersPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.orderItems}
                    element={
                        <LazyPage>
                            <OrderItemsPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.loyalty}
                    element={
                        <LazyPage>
                            <LoyaltyPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.b2b}
                    element={
                        <LazyPage>
                            <B2BPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.wholesalePromotions}
                    element={
                        <LazyPage>
                            <WholesalePromotionsPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.wholesaleSuppliers}
                    element={
                        <LazyPage>
                            <WholesaleSuppliersPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.campaigns}
                    element={
                        <LazyPage>
                            <CampaignsPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.bi}
                    element={
                        <LazyPage>
                            <ReportsPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.retailCockpit}
                    element={
                        <LazyPage>
                            <RetailCockpitPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.onlineCockpit}
                    element={
                        <LazyPage>
                            <OnlineCockpitPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.retailFrequency}
                    element={
                        <LazyPage>
                            <RetailFrequencyReportPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.retailPercentTargets}
                    element={
                        <LazyPage>
                            <RetailPercentTargetsPage />
                        </LazyPage>
                    }
                />
                <Route
                    path={routePaths.retailDefectsSliven}
                    element={
                        <LazyPage>
                            <RetailDefectsSlivenPage />
                        </LazyPage>
                    }
                />
                <Route path={routePaths.expenses} element={<ExpensesLayout />}>
                    <Route index element={<Navigate to={routePaths.expensesTransport} replace />} />
                    <Route
                        path="transport"
                        element={
                            <LazyPage>
                                <ExpensesTransportPage />
                            </LazyPage>
                        }
                    />
                    <Route
                        path="marketing"
                        element={
                            <LazyPage>
                                <ExpensesMarketingPage />
                            </LazyPage>
                        }
                    />
                    <Route
                        path="salaries"
                        element={
                            <LazyPage>
                                <ExpensesSalariesPage />
                            </LazyPage>
                        }
                    />
                </Route>
                <Route path={routePaths.grafica} element={<Navigate to={routePaths.bi} replace />} />
                <Route path={routePaths.grafica2} element={<Navigate to={routePaths.bi} replace />} />
                <Route
                    path={routePaths.changePassword}
                    element={
                        <LazyPage>
                            <ChangePasswordPage />
                        </LazyPage>
                    }
                />
            </Route>
            <Route path="*" element={<Navigate to={routePaths.root} replace />} />
        </Routes>
    );
}
